import { Conversation } from '@nextforce/api'
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs'
import { getFocusedRouteNameFromRoute } from '@react-navigation/native'
import { Icon, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { useConversation } from 'chat/conversations/queries/detail'
import { isRepository } from 'chat/conversations/utils'
import { useFileFolderActions } from 'file_folder_repository/context'
import { FileFoldersBottomTabProps } from 'home/stack/types'
import React, { useLayoutEffect } from 'react'
import { Platform, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import {
  IconButton,
  showHeaderButtonList,
} from 'shared/header/header_button_list'
import { ResourceAcknowledgementListScreen } from '../acknowledgements/screen'
import { ChatGroupFoldersScreen } from '../screen'
import { SharedFileFoldersScreen } from '../shared_file_folders/screen'
import { FileFoldersBottomTabParamList } from './types'

const FileFoldersBottomTabNavigator =
  createBottomTabNavigator<FileFoldersBottomTabParamList>()
const BOTTOM_TAB_HEIGHT = 70

export function FileFoldersBottomTab({
  navigation,
  route,
}: FileFoldersBottomTabProps) {
  const { theme } = useTheme()
  const { handleNavigation } = useRoutingContext()
  const styles = useStyles()
  const initialRoute: keyof FileFoldersBottomTabParamList = 'files'
  const conversationId = route.params.conversationId
  const conversationQuery = useConversation(conversationId)
  const { openSortOrderBottomSheet } = useFileFolderActions()
  const currentTab = getFocusedRouteNameFromRoute(route) ?? initialRoute
  const TabScreens = getTabs(conversationQuery.data)
  const { bottom } = useSafeAreaInsets()

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: getHeaderTitle(route, initialRoute, conversationQuery.data),
      headerTitleContainerStyle: {
        flexShrink: 1,
      },
    })
  }, [
    conversationQuery.data,
    handleNavigation,
    initialRoute,
    navigation,
    route,
  ])

  useLayoutEffect(() => {
    const headerButtons: IconButton[] = []

    if (currentTab === 'files') {
      headerButtons.push({
        onPress: () => openSortOrderBottomSheet(),
        name: 'sort',
        type: 'material-icons',
      })
    }

    if (isRepository(conversationQuery?.data)) {
      headerButtons.push({
        onPress: () => {
          handleNavigation({
            routeName: 'conversation_details',
            routeParams: { conversationId },
          })
        },
        name: 'information-circle-outline',
        type: 'ionicon',
      })
    }

    showHeaderButtonList({
      navigation,
      buttons: headerButtons,
      theme,
    })
  }, [
    conversationId,
    conversationQuery,
    currentTab,
    handleNavigation,
    navigation,
    openSortOrderBottomSheet,
    theme,
  ])

  return (
    <FileFoldersBottomTabNavigator.Navigator
      initialRouteName={initialRoute}
      backBehavior="none"
      screenOptions={props => ({
        cardStyle: { backgroundColor: theme.colors.white },
        headerShown: false,
        tabBarShowLabel: false,
        tabBarIconStyle: styles.tabBarIcon,
        tabBarStyle:
          TabScreens.length === 1
            ? { display: 'none' }
            : { height: BOTTOM_TAB_HEIGHT + bottom },
        tabBarButton: ({ children, onPress, ...rest }) => (
          <PressableOpacity
            style={styles.tabBarItemContainer}
            onPress={e => {
              if (Platform.OS === 'web') {
                e.preventDefault?.()
              }
              onPress?.(e)
            }}
            {...rest}
          >
            {children}
          </PressableOpacity>
        ),
        tabBarIcon: ({ focused }) => {
          let BottomTabIcon: React.FC = () => null
          let label
          switch (props.route.name) {
            case 'files':
              BottomTabIcon = () => (
                <Icon name="folder" type="feather" size={24} />
              )
              label = 'Files'
              break
            case 'shared':
              BottomTabIcon = () => (
                <Icon name="users" type="feather" size={24} />
              )
              label = 'Shared'
              break
            case 'acknowledgements':
              label = 'Acks'
              BottomTabIcon = () => (
                <Icon
                  name="account-eye-outline"
                  type="material-design"
                  size={24}
                />
              )
          }

          return (
            <View style={styles.tabBarIconLabelContainer}>
              <View
                style={[
                  styles.tab,
                  {
                    backgroundColor: focused
                      ? theme.colors?.grey5
                      : 'transparent',
                  },
                ]}
              >
                <View style={styles.iconBox}>
                  <BottomTabIcon />
                </View>
              </View>
              <Text
                style={[
                  styles.label,
                  {
                    color: focused ? theme.colors.black : 'grey',
                    fontWeight: focused ? 'bold' : 'normal',
                  },
                ]}
                numberOfLines={1}
              >
                {label}
              </Text>
            </View>
          )
        },
      })}
    >
      {TabScreens}
    </FileFoldersBottomTabNavigator.Navigator>
  )
}

function getHeaderTitle(
  route: any,
  initialRoute: string,
  conversation: Conversation | undefined
) {
  const routeName = getFocusedRouteNameFromRoute(route) ?? initialRoute
  switch (routeName) {
    case 'files':
      if (isRepository(conversation)) {
        return conversation?.name
      }
      return t('documents')
    case 'shared':
      return t('shared')
  }
  return t(routeName)
}

function getTabs(conversation: Conversation | undefined) {
  const tabs: Record<keyof FileFoldersBottomTabParamList, React.JSX.Element> = {
    files: (
      <FileFoldersBottomTabNavigator.Screen
        key={'files'}
        component={ChatGroupFoldersScreen}
        name="files"
        initialParams={{ conversationId: conversation?.id }}
      />
    ),
    acknowledgements: (
      <FileFoldersBottomTabNavigator.Screen
        key="acknowledgements"
        component={ResourceAcknowledgementListScreen}
        name="acknowledgements"
        initialParams={{ conversationId: conversation?.id }}
      />
    ),
    shared: (
      <FileFoldersBottomTabNavigator.Screen
        key={2}
        component={SharedFileFoldersScreen}
        name="shared"
        initialParams={{ conversationId: conversation?.id }}
      />
    ),
  }

  const groupedTabs = [tabs.files]

  if (isRepository(conversation) && conversation?.showAcknowledgements) {
    groupedTabs.push(tabs.acknowledgements)
  }
  return groupedTabs
}

const useStyles = makeStyles(theme => ({
  tabBarItemContainer: {
    alignItems: 'center',
    flex: 1,
    marginTop: 5,
  },
  tabBarIconLabelContainer: { flex: 1, alignItems: 'center' },
  iconBox: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    alignSelf: 'stretch',
    textAlign: 'center',
    fontSize: 12,
    overflow: 'visible',
    marginTop: 2,
  },
  tabBarIcon: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tab: {
    padding: 5,
    paddingHorizontal: 10,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
}))
